/*
 * Safegear - delivery & installation estimate updater (Horizon 4.1.3)
 *
 * WHY THIS EXISTS
 * The estimates are weight-banded. When the buyer changes variant the section must
 * re-price against the newly selected variant's weight, or the quote can understate
 * installation by several bands.
 *
 * WHAT WAS WRONG WITH THE 2026.07.27 VERSION  (read before "simplifying" this file)
 * It listened for the correct event and did use event.promise - both of those are
 * fine, contrary to what the 2026.07.27 handover records. What it actually did was
 * read the variant id out of the resolved payload and look it up in a variant-id ->
 * weight map that the section had serialized into a data attribute. Those two ids are
 * not the same shape:
 *
 *   - ProductSelectEvent's constructor rewrites the resolved payload's variant id into
 *     a GID: `id: createGid('ProductVariant', variant.id)`. Verified in
 *     https://cdn.shopify.com/storefront/standard-events.js
 *   - The section serialized `"{{ v.id }}"`, a bare numeric id.
 *
 * Every lookup missed, and the updater's deliberate "unknown variant - keep the last
 * known prices" branch returned silently, with no console error. The symptom was a
 * listener that looked like it had never fired.
 *
 * This rebuild does not look up variant ids at all, so the whole class of bug is gone.
 *
 * HOW HORIZON ACTUALLY WORKS  (read assets/variant-picker.js before editing)
 *   - <variant-picker> dispatches ProductSelectEvent from '@shopify/events' on itself.
 *     It bubbles, so a document-level listener works.
 *   - The payload is NOT on the event at dispatch time. Await `event.promise`, which
 *     resolves to { variant, detail: { html, productId, newProduct, sourceId, resource } }.
 *   - On a normal product page buildRequestUrl() returns the product URL with
 *     `option_values` and NO section_id, so `detail.html` is the FULL new page document.
 *     Horizon morphs only the variant picker; every other component reads what it needs
 *     out of detail.html. That is the cheap path and the one used here: the new
 *     server-rendered weight is already sitting in that document.
 *   - `detail.resource` is the parsed variant JSON and `resource.id` IS numeric. The
 *     top-level `variant.id` is the GID. Only the former is safe in a URL.
 *
 * Prices are painted client-side by delivery-pricing-renderer.js, so lifting markup
 * (the metafield-table approach) would only copy the "Loading prices..." shell across.
 * We lift the data attributes instead and re-run the renderer.
 */

import { StandardEvents } from '@shopify/events';
import { sectionRenderer } from '@theme/section-renderer';

const ROOT = '.installation-delivery-section[data-enable-dynamic="true"]';

/**
 * Attributes the renderer reads. `pricingJson` is country-derived rather than
 * variant-derived, but lifting it costs nothing and keeps this correct if a future
 * change makes it variant-dependent.
 */
const LIFTED = ['weightKg', 'variantId', 'pricingJson'];

/**
 * Re-run the estimate renderer over every section on the page. Its constructor renders
 * all of them, so this is a full refresh in one call.
 */
function repaint() {
  const Renderer = window.DeliveryPricingRenderer;

  if (typeof Renderer !== 'function') {
    console.warn('[sg-delivery] DeliveryPricingRenderer not loaded; estimates not updated.');
    return;
  }

  new Renderer();
}

/**
 * Preferred path: copy the newly rendered data attributes out of detail.html.
 * Sections are queried fresh on every event - Horizon may have morphed the DOM, so a
 * NodeList cached at load time would go stale.
 *
 * @param {Document | undefined} html
 * @returns {boolean} true when at least one section was updated.
 */
function updateFromHtml(html) {
  if (!html || typeof html.querySelectorAll !== 'function') return false;

  const targets = document.querySelectorAll(ROOT);
  const sources = html.querySelectorAll(ROOT);
  if (!targets.length || !sources.length) return false;

  let updated = 0;

  targets.forEach((target, index) => {
    const source = sources[index] || sources[0];

    for (const key of LIFTED) {
      const next = source.dataset[key];
      if (next !== undefined) target.dataset[key] = next;
    }

    updated += 1;
  });

  return updated > 0;
}

/**
 * Fallback: ask Horizon to re-render this section on its own. Needed where the picker
 * requested a single section (quick add, product cards, featured product), so
 * detail.html does not contain us.
 *
 * The morph restores the server-rendered shell, which reads "Loading prices...", so the
 * repaint afterwards is required rather than belt-and-braces.
 *
 * @param {string | number | undefined} variantId - numeric id from detail.resource
 */
function updateViaRenderer(variantId) {
  const roots = document.querySelectorAll(ROOT);
  if (!roots.length) return;

  const url = new URL(window.location.href);
  if (variantId) url.searchParams.set('variant', String(variantId));

  roots.forEach((root) => {
    const section = root.closest('.shopify-section');
    if (!section || !section.id) return;

    sectionRenderer
      .renderSection(section.id, { url, cache: false })
      .then(repaint)
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn('[sg-delivery] section re-render failed:', error);
        }
      });
  });
}

/**
 * @param {Event & { promise?: Promise<any> }} event
 */
function onProductSelect(event) {
  // Variant swatches on product cards dispatch this too; ignore them.
  if (event.target instanceof Element && event.target.closest('product-card')) return;

  const promise = event.promise;
  if (!promise || typeof promise.then !== 'function') {
    console.warn('[sg-delivery] productSelect fired without a promise; nothing to do.');
    return;
  }

  promise
    .then(({ detail }) => {
      if (updateFromHtml(detail?.html)) {
        repaint();
        return;
      }

      // detail.resource.id is numeric. Do NOT substitute the top-level variant.id here:
      // it is a GID and would produce an invalid ?variant= parameter.
      updateViaRenderer(detail?.resource?.id);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') {
        console.warn('[sg-delivery] product select promise rejected:', error);
      }
    });
}

document.addEventListener(StandardEvents.productSelect, onProductSelect);
